import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, MapPin, Download, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { ShareEvent } from '@/types/share';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDebounce } from '@/hooks/useDebounce';

interface ShareEventsTableProps {
  events: ShareEvent[];
}

type SortField = 'createdAt' | 'cityRegion' | 'country' | null;
type SortDirection = 'asc' | 'desc';

export function ShareEventsTable({ events }: ShareEventsTableProps) {
  useLanguage();
  const [searchEmail, setSearchEmail] = useState('');
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const debouncedSearch = useDebounce(searchEmail, 300);

  // Aplicar filtros
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      if (debouncedSearch && !event.recipientEmail.toLowerCase().includes(debouncedSearch.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [events, debouncedSearch]);

  // Ordenar eventos
  const sortedEvents = useMemo(() => {
    if (!sortField) return filteredEvents;
    
    return [...filteredEvents].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      
      switch (sortField) {
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'cityRegion':
          aValue = `${a.geo.city}, ${a.geo.region}`;
          bValue = `${b.geo.city}, ${b.geo.region}`;
          break;
        case 'country':
          aValue = a.geo.country;
          bValue = b.geo.country;
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return sortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredEvents, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
  useLanguage();
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1.5 h-3 w-3 text-[#AEB8C9]" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="ml-1.5 h-3 w-3 text-[#4B7BFF]" />
      : <ArrowDown className="ml-1.5 h-3 w-3 text-[#4B7BFF]" />;
  };

  const handleExportCsv = () => {
    if (!sortedEvents.length) return;

    const headers = ['E-mail', 'Data/Hora', 'Cidade', 'Estado', 'País'];
    const rows = sortedEvents.map(event => [
      event.recipientEmail,
      format(new Date(event.createdAt), "Pp", { locale: getInterfaceDateLocale() }),
      event.geo.city,
      event.geo.region,
      event.geo.country
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
      
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historico-eventos-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{translateInterfaceText("Histórico de Eventos")}</CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleExportCsv}
            disabled={sortedEvents.length === 0}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {translateInterfaceText("Exportar")}</Button>
        </div>
        
        {/* Filtro de busca */}
        <div className="mt-4">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={translateInterfaceText("Buscar e-mail...")}
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead className="whitespace-nowrap">{translateInterfaceText("E-mail")}</TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort('createdAt')}
                    className="flex items-center hover:text-foreground transition-colors"
                  >
                    {translateInterfaceText("Data/Hora")}<SortIcon field="createdAt" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort('cityRegion')}
                    className="flex items-center hover:text-foreground transition-colors"
                  >
                    {translateInterfaceText("Cidade/UF")}<SortIcon field="cityRegion" />
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('country')}
                    className="flex items-center justify-end hover:text-foreground transition-colors w-full"
                  >
                    {translateInterfaceText("País")}<SortIcon field="country" />
                  </button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedEvents.slice(0, 20).map(event => (
                <TableRow key={event.id}>
                  <TableCell className="font-medium">
                    {event.recipientEmail}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(event.createdAt), "Pp", { locale: getInterfaceDateLocale() })}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>
                        {event.geo.city}, {event.geo.region}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-right">
                    {event.geo.country}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {sortedEvents.length > 20 && (
          <p className="text-sm text-muted-foreground text-center mt-4">
            {translateInterfaceText("Mostrando 20 de")}{" "}{sortedEvents.length} {" "}{translateInterfaceText("eventos")}</p>
        )}

        {sortedEvents.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            {translateInterfaceText("Nenhum evento encontrado para este filtro.")}</p>
        )}
      </CardContent>
    </Card>
  );
}
